'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import InvitationToast from './InvitationToast';

type Notification =
  | {
      id: string;
      type: 'invitation';
      data: {
        invitationId: string;
        organizationId: string;
        organizationName: string;
        organizationLogo?: string | null;
        roles: string;
        onAccept: () => Promise<void>;
        onDecline: () => void;
      };
    }
  | {
      id: string;
      type: 'success' | 'error';
      message: string;
    };

interface NotificationContextType {
  showInvitation: (data: Extract<Notification, { type: 'invitation' }>['data']) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  removeNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showInvitation = (data: Extract<Notification, { type: 'invitation' }>['data']) => {
    const id = `invitation-${data.invitationId}`;

    // Use functional state update to prevent race conditions
    setNotifications((prev) => {
      // Don't add duplicate notifications
      if (prev.some((n) => n.id === id)) return prev;

      return [
        ...prev,
        {
          id,
          type: 'invitation' as const,
          data,
        },
      ];
    });
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const showSuccess = (message: string) => {
    const id = `success-${Date.now()}`;
    setNotifications((prev) => [
      ...prev,
      {
        id,
        type: 'success' as const,
        message,
      },
    ]);

    // Auto-remove after 5 seconds
    setTimeout(() => removeNotification(id), 5000);
  };

  const showError = useCallback((message: string) => {
    const id = `error-${Date.now()}`;
    setNotifications((prev) => [
      ...prev,
      {
        id,
        type: 'error' as const,
        message,
      },
    ]);

    // Auto-remove after 7 seconds (errors stay longer)
    setTimeout(() => removeNotification(id), 7000);
  }, []);

  // Listen for session expired events from fetchWithBypass
  useEffect(() => {
    const handleSessionExpired = () => {
      showError('Your session has expired. Please refresh the page and log in again.');
    };

    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, [showError]);

  return (
    <NotificationContext.Provider
      value={{ showInvitation, showSuccess, showError, removeNotification }}
    >
      {children}

      {/* Global notification live region */}
      <div
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 z-50 flex items-end px-4 py-6 sm:items-start sm:p-6"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          {notifications.map((notification) => {
            if (notification.type === 'invitation') {
              return (
                <InvitationToast
                  key={notification.id}
                  id={notification.id}
                  organizationName={notification.data.organizationName}
                  organizationLogo={notification.data.organizationLogo}
                  roles={notification.data.roles}
                  onAccept={notification.data.onAccept}
                  onDecline={notification.data.onDecline}
                  onClose={() => removeNotification(notification.id)}
                />
              );
            }

            if (notification.type === 'success' || notification.type === 'error') {
              return (
                <SimpleToast
                  key={notification.id}
                  type={notification.type}
                  message={notification.message}
                  onClose={() => removeNotification(notification.id)}
                />
              );
            }

            return null;
          })}
        </div>
      </div>
    </NotificationContext.Provider>
  );
}

// Simple toast component for success/error messages
function SimpleToast({
  type,
  message,
  onClose,
}: {
  type: 'success' | 'error';
  message: string;
  onClose: () => void;
}) {
  const { darkMode } = useDarkMode();
  const icon = type === 'success' ? '✓' : '✕';

  const bgColor =
    type === 'success'
      ? darkMode
        ? 'bg-green-900/20'
        : 'bg-green-50'
      : darkMode
        ? 'bg-red-900/20'
        : 'bg-red-50';

  const textColor =
    type === 'success'
      ? darkMode
        ? 'text-green-400'
        : 'text-green-800'
      : darkMode
        ? 'text-red-400'
        : 'text-red-800';

  const iconBgColor =
    type === 'success'
      ? darkMode
        ? 'bg-green-600'
        : 'bg-green-400'
      : darkMode
        ? 'bg-red-600'
        : 'bg-red-400';

  return (
    <div
      className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg shadow-lg ring-1 ${
        darkMode ? 'bg-gray-800 ring-white/10' : 'bg-white ring-black/5'
      }`}
    >
      <div className={`p-4 ${bgColor}`}>
        <div className="flex items-start">
          <div className="shrink-0">
            <span
              className={`inline-flex size-8 items-center justify-center rounded-full ${iconBgColor} font-bold text-white`}
            >
              {icon}
            </span>
          </div>
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <p className={`text-sm font-medium ${textColor}`}>{message}</p>
          </div>
          <div className="ml-4 flex shrink-0">
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex rounded-md ${textColor} hover:opacity-75 focus:ring-2 focus:ring-offset-2 focus:outline-hidden ${type === 'success' ? 'focus:ring-green-500' : 'focus:ring-red-500'}`}
            >
              <span className="sr-only">Close</span>
              <svg className="size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
